import _ from 'lodash';

import { useEffect } from 'react';
import { AutoUpdateTimeSchedule } from '@/schedule/AutoUpdateTimeSchedule.molecule';
import { IrregularSchedule } from '@/schedule/IrregularSchedule.atom';
import { LiveSchedule } from '@/schedule/LiveSchedule.atom';
import { SelectScheduleType } from '@/schedule/SelectScheduleType.molecule';
import { useSchedule } from '@/schedule/useSchedule.hook';
import type { DurationValuesByUnit } from '@/core/DurationSelector.atom';
import type { Timezone } from '@/datetime/timezone.service';
import type { ScheduleTypeTranslations } from '@/schedule/SelectScheduleType.molecule';
import type { LiveUpdateRate, ScheduleTypeName, UpdatedSchedule } from '@/schedule/schedule.types';

export interface ScheduleProps {
  cronSchedule: string[] | undefined;
  initialScheduleType: ScheduleTypeName;
  timezoneDefault?: Timezone;
  updatedSchedule: UpdatedSchedule | undefined;
  onScheduleChange: (setSchedule: UpdatedSchedule) => void;
  defaultLiveRate?: LiveUpdateRate;
  selectScheduleTypeHelpContent?: React.ReactNode;
  showLiveRateHelp?: boolean;
  scheduleTypeTranslations?: ScheduleTypeTranslations;
  durationValuesByUnit?: DurationValuesByUnit;
  disabled?: boolean;
}

const emptyDefaultObject = {};
export const Schedule = ({
  cronSchedule,
  initialScheduleType,
  timezoneDefault,
  defaultLiveRate,
  updatedSchedule,
  onScheduleChange,
  selectScheduleTypeHelpContent,
  showLiveRateHelp = false,
  scheduleTypeTranslations = emptyDefaultObject,
  durationValuesByUnit = emptyDefaultObject,
  disabled = false,
}: ScheduleProps) => {
  const {
    selectedScheduleType,
    setSelectedScheduleType,
    isLive,
    liveRate,
    setLiveRate,
    onLiveScheduleInvalidInput,
    timeEntries,
    setTimeEntries,
    timezone,
    setTimezone,
    irregularSchedule,
    setIrregularSchedule,
  } = useSchedule(cronSchedule, updatedSchedule, initialScheduleType, timezoneDefault, defaultLiveRate);

  useEffect(() => {
    onScheduleChange({ liveRate, selectedScheduleType, irregularSchedule, timeEntries, timezone });
  }, [liveRate, selectedScheduleType, irregularSchedule, timeEntries, timezone, onScheduleChange]);

  if (!_.isNil(irregularSchedule)) {
    return (
      <IrregularSchedule
        schedules={irregularSchedule}
        onConvertIt={() => setIrregularSchedule(undefined)}
        disabled={disabled}
      />
    );
  }

  return (
    <div data-testid="schedule">
      <SelectScheduleType
        scheduleType={selectedScheduleType}
        setScheduleType={setSelectedScheduleType}
        helpContent={selectScheduleTypeHelpContent}
        scheduleTypeTranslations={scheduleTypeTranslations}
        disabled={disabled}
      />
      {!isLive && (
        <AutoUpdateTimeSchedule
          timezone={timezone}
          setTimezone={setTimezone}
          entries={timeEntries}
          setEntries={setTimeEntries}
          disabled={disabled}
        />
      )}
      {isLive && (
        <LiveSchedule
          rate={liveRate}
          onChange={setLiveRate}
          onInvalidInput={onLiveScheduleInvalidInput}
          showHelp={showLiveRateHelp}
          durationValuesByUnit={durationValuesByUnit}
          disabled={disabled}
        />
      )}
    </div>
  );
};
